// ─── WorkRoamer™ Master Bug Tracker™ — Data Layer ────────────────────────────
//
// Persistent bug registry for the WorkRoamer development environment.
// Storage: nomad:bug-tracker:v1 (localStorage)
// Accessible via: Dev Settings → Quality Assurance → Master Bug Tracker™

const STORAGE_KEY  = 'nomad:bug-tracker:v1';
const SEEDED_KEY   = 'nomad:bug-tracker:seeded:v1';
const BUILD_VER    = '2.47.0';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BugSeverity   = 'critical' | 'high' | 'medium' | 'low';
export type BugStatus     = 'open' | 'in-progress' | 'fixed' | 'verified' | 'deferred' | 'wont-fix';
export type BugPriority   = 'release-blocker' | 'high' | 'medium' | 'low';
export type BugModule     =
  | 'auth' | 'ai' | 'vault' | 'missions' | 'profile' | 'security'
  | 'database' | 'api' | 'mobile' | 'performance' | 'storage'
  | 'onboarding' | 'intelligence' | 'quality' | 'platform';

export interface BugHistoryEntry {
  timestamp: string;
  action:    string;
  from?:     string;
  to?:       string;
  by:        string;
  note?:     string;
}

/** Extension point — populated by future beta tester intake */
export interface BetaReportTemplate {
  reporter:           string;
  device:             string;
  browser:            string;
  platform:           string;
  buildNumber:        string;
  screenshotUrl?:     string;
  stepsToReproduce:   string;
  expectedBehavior:   string;
  actualBehavior:     string;
  severity:           BugSeverity;
}

export interface Bug {
  id:               string;
  title:            string;
  description:      string;
  module:           BugModule;
  severity:         BugSeverity;
  status:           BugStatus;
  priority:         BugPriority;
  affectedFiles:    string[];
  notes:            string;
  history:          BugHistoryEntry[];
  screenshotUrls:   string[];          // extension point
  auditReferences:  string[];          // e.g. 'AI_SECURITY_PLATFORM_AUDIT_v2.35.md'
  versionFound:     string;
  versionFixed?:    string;
  reporter:         string;
  assignee?:        string;
  createdAt:        string;
  updatedAt:        string;
  // Release gate flags
  isReleaseBlocker: boolean;
  isSecurityBlocker: boolean;
  isDataLossBug:    boolean;
  isAuthBug:        boolean;
  isAIBlocker:      boolean;
  // Beta tester extension point
  betaReport?:      BetaReportTemplate;
}

export interface BugFilter {
  search?:   string;
  module?:   BugModule | 'all';
  severity?: BugSeverity | 'all';
  status?:   BugStatus | 'all';
  priority?: BugPriority | 'all';
}

export interface ReleaseGateResult {
  clear:            boolean;
  criticalBugs:     Bug[];
  securityBlockers: Bug[];
  dataLossBugs:     Bug[];
  authBugs:         Bug[];
  aiBlockers:       Bug[];
  releaseBlockers:  Bug[];
  summary:          string;
}

// ─── Pre-seeded bug data ─────────────────────────────────────────────────────

function ts(offset = 0): string {
  return new Date(Date.now() - offset).toISOString();
}

const SEED_BUGS: Omit<Bug, 'id'>[] = [
  // ── Fixed historical bugs ──────────────────────────────────────────────────
  {
    title: 'First-run activation race — Radix Dialog pointer-event lock',
    description: 'Radix <Dialog modal> applied pointer-events:none to document.body during first-boot wizard, locking the entire UI. Compounded by a legacy wizard race condition where two activation paths fired simultaneously.',
    module: 'onboarding', severity: 'critical', status: 'verified', priority: 'release-blocker',
    affectedFiles: ['src/pages/Home.tsx', 'src/hooks/useFirstBoot.ts'],
    notes: '3-layer fix: Gate 3 in Home.tsx + nomad:wizard-active sentinel + useFirstBoot guard',
    history: [
      { timestamp: ts(86400000 * 14), action: 'opened', by: 'system', note: 'Reported via beta session' },
      { timestamp: ts(86400000 * 12), action: 'status_change', from: 'open', to: 'in-progress', by: 'engineer' },
      { timestamp: ts(86400000 * 10), action: 'status_change', from: 'in-progress', to: 'fixed', by: 'engineer' },
      { timestamp: ts(86400000 * 8),  action: 'status_change', from: 'fixed', to: 'verified', by: 'engineer' },
    ],
    screenshotUrls: [], auditReferences: [],
    versionFound: '2.33.0', versionFixed: '2.34.0',
    reporter: 'system', assignee: 'engineer',
    createdAt: ts(86400000 * 14), updatedAt: ts(86400000 * 8),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'Workspace empty state — missions writing to user_id=1 (seed user)',
    description: 'HARVESTER_USER_ID mismatch caused all harvested missions to be written with user_id=1 (the DB seed user) instead of the authenticated user. Real users saw 0 missions. The "133 missions" count shown in the overlay was the pipeline JSON count — not DB rows for the real user.',
    module: 'missions', severity: 'critical', status: 'verified', priority: 'release-blocker',
    affectedFiles: ['artifacts/api-server/src/routes/ops.ts', 'src/services/harvesters/'],
    notes: 'Fix: pass req.userId to runner instead of hardcoded HARVESTER_USER_ID',
    history: [
      { timestamp: ts(86400000 * 28), action: 'opened', by: 'system', note: 'All users reported empty workspace' },
      { timestamp: ts(86400000 * 26), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer' },
      { timestamp: ts(86400000 * 24), action: 'status_change', from: 'fixed', to: 'verified', by: 'engineer' },
    ],
    screenshotUrls: [], auditReferences: [],
    versionFound: '2.29.0', versionFixed: '2.30.0',
    reporter: 'system', assignee: 'engineer',
    createdAt: ts(86400000 * 28), updatedAt: ts(86400000 * 24),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: true, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'Pipeline timeout — FirstBootOverlay stuck on dark blue screen',
    description: 'runOpportunityPipeline() had no timeout. On slow connections or harvester hangs, the FirstBootOverlay would spin indefinitely — users saw a dark blue screen with no escape path.',
    module: 'onboarding', severity: 'high', status: 'verified', priority: 'high',
    affectedFiles: ['artifacts/api-server/src/services/opportunityPipeline.ts'],
    notes: 'Fix: 120s AbortController timeout in runOpportunityPipeline()',
    history: [
      { timestamp: ts(86400000 * 35), action: 'opened', by: 'system' },
      { timestamp: ts(86400000 * 33), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer' },
      { timestamp: ts(86400000 * 30), action: 'status_change', from: 'fixed', to: 'verified', by: 'engineer' },
    ],
    screenshotUrls: [], auditReferences: [],
    versionFound: '2.28.0', versionFixed: '2.29.0',
    reporter: 'system', assignee: 'engineer',
    createdAt: ts(86400000 * 35), updatedAt: ts(86400000 * 30),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'Production: trust proxy misconfiguration — shared rate-limit bucket',
    description: 'Trust proxy not configured on the Express app, causing all requests to share the same IP (Replit proxy IP) for rate limiting. All users were throttled together, effectively breaking the API for all users simultaneously.',
    module: 'api', severity: 'critical', status: 'verified', priority: 'release-blocker',
    affectedFiles: ['artifacts/api-server/src/app.ts'],
    notes: 'Fix: app.set("trust proxy", 1) + X-Forwarded-For extraction in rate limiter',
    history: [
      { timestamp: ts(86400000 * 21), action: 'opened', by: 'system', note: 'Production incident July 2026' },
      { timestamp: ts(86400000 * 20), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer' },
      { timestamp: ts(86400000 * 18), action: 'status_change', from: 'fixed', to: 'verified', by: 'engineer' },
    ],
    screenshotUrls: [], auditReferences: ['AI_SECURITY_PLATFORM_AUDIT_v2.35.md'],
    versionFound: '2.22.0', versionFixed: '2.23.0',
    reporter: 'system', assignee: 'engineer',
    createdAt: ts(86400000 * 21), updatedAt: ts(86400000 * 18),
    isReleaseBlocker: false, isSecurityBlocker: true, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'Mobile viewport — horizontal scroll + overflow on 375px devices',
    description: 'WorkRoamerBrand component missing maxWidth:100%. Auth pages had no overflow guard. OCW footer button overflowed at 375px. No AppErrorBoundary wrapping the App component.',
    module: 'mobile', severity: 'high', status: 'verified', priority: 'high',
    affectedFiles: ['src/components/WorkRoamerBrand.tsx', 'src/pages/SignupPage.tsx', 'src/main.tsx'],
    notes: 'Fix: maxWidth:100% on brand, overflow guard on auth pages, AppErrorBoundary in main.tsx',
    history: [
      { timestamp: ts(86400000 * 42), action: 'opened', by: 'system' },
      { timestamp: ts(86400000 * 40), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer' },
      { timestamp: ts(86400000 * 38), action: 'status_change', from: 'fixed', to: 'verified', by: 'engineer' },
    ],
    screenshotUrls: [], auditReferences: [],
    versionFound: '2.23.0', versionFixed: '2.24.0',
    reporter: 'system', assignee: 'engineer',
    createdAt: ts(86400000 * 42), updatedAt: ts(86400000 * 38),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'Pipeline auth — POST /ops/run-all returned 403 for new users',
    description: 'The /ops/run-all endpoint was protected by requireRole(BETA_TESTER) but new users register with the USER role. This caused every new signup to get a 403 on the pipeline trigger, resulting in an empty workspace with no error visible to the user.',
    module: 'auth', severity: 'high', status: 'verified', priority: 'high',
    affectedFiles: ['artifacts/api-server/src/routes/ops.ts'],
    notes: 'Fix: changed to requireAuth (any authenticated user can trigger their own pipeline)',
    history: [
      { timestamp: ts(86400000 * 50), action: 'opened', by: 'system' },
      { timestamp: ts(86400000 * 48), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer' },
    ],
    screenshotUrls: [], auditReferences: [],
    versionFound: '2.20.0', versionFixed: '2.21.0',
    reporter: 'system', assignee: 'engineer',
    createdAt: ts(86400000 * 50), updatedAt: ts(86400000 * 48),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: false, isAuthBug: true, isAIBlocker: false,
  },
  {
    title: 'Fresh signup — stale localStorage persona blocks onboarding wizard',
    description: 'If a user had previously used the app (or a prior account) and localStorage/Supabase had a persona record, the wizard was bypassed for new accounts. New users were dropped directly into the main app with no onboarding.',
    module: 'onboarding', severity: 'high', status: 'verified', priority: 'high',
    affectedFiles: ['src/hooks/useFirstBoot.ts', 'src/services/HydrationOrchestrator.ts'],
    notes: 'Fix: nomad:fresh-signup flag bypasses all persona checks on new registration',
    history: [
      { timestamp: ts(86400000 * 18), action: 'opened', by: 'system' },
      { timestamp: ts(86400000 * 16), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer' },
      { timestamp: ts(86400000 * 14), action: 'status_change', from: 'fixed', to: 'verified', by: 'engineer' },
    ],
    screenshotUrls: [], auditReferences: [],
    versionFound: '2.31.0', versionFixed: '2.32.0',
    reporter: 'system', assignee: 'engineer',
    createdAt: ts(86400000 * 18), updatedAt: ts(86400000 * 14),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },

  // ── Open issues ────────────────────────────────────────────────────────────
  {
    title: 'Content-Security-Policy (CSP) header not configured',
    description: 'The API server does not set a Content-Security-Policy header. Without CSP, the app is vulnerable to XSS attacks that bypass DOMPurify (e.g., via injected <script> tags from third-party content). Identified in Platform Audit v2.35.',
    module: 'security', severity: 'high', status: 'fixed', priority: 'high',
    affectedFiles: ['artifacts/api-server/src/app.ts', 'artifacts/nomad/vite.config.ts'],
    notes: 'Fixed (Production Readiness Sprint 1): API server: default-src \'none\'; frame-ancestors \'none\' (strict — JSON API only). SPA dev server: scoped CSP in Vite server.headers allowing self + data: + wss: + blob:.',
    history: [
      { timestamp: ts(86400000 * 1), action: 'opened', by: 'audit', note: 'Identified in AI_SECURITY_PLATFORM_AUDIT_v2.35.md' },
      { timestamp: ts(0), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer', note: 'P1 — Production Readiness Sprint 1. CSP added to app.ts + Vite config.' },
    ],
    screenshotUrls: [], auditReferences: ['AI_SECURITY_PLATFORM_AUDIT_v2.35.md'],
    versionFound: '2.35.0', versionFixed: '2.36.0',
    reporter: 'audit-system', assignee: 'engineer',
    createdAt: ts(86400000), updatedAt: ts(0),
    isReleaseBlocker: false, isSecurityBlocker: true, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'CSRF protection insufficient — SameSite=Lax only, no token',
    description: 'Session cookie is SameSite=Lax which provides partial CSRF mitigation but does not cover all attack vectors (e.g., top-level navigation POSTs from third-party sites). No CSRF synchronizer token or double-submit cookie is implemented.',
    module: 'security', severity: 'high', status: 'fixed', priority: 'high',
    affectedFiles: ['artifacts/api-server/src/middlewares/csrfProtection.ts', 'artifacts/api-server/src/routes/auth.ts', 'artifacts/nomad/src/lib/csrfInterceptor.ts', 'artifacts/nomad/src/main.tsx'],
    notes: 'Fixed (Production Readiness Sprint 1): HMAC-derived double-submit token pattern. wr_csrf non-HttpOnly cookie set on every auth response. X-CSRF-Token header validated on POST/PUT/PATCH/DELETE. Global fetch interceptor in frontend adds header automatically. Pre-auth routes exempt.',
    history: [
      { timestamp: ts(86400000), action: 'opened', by: 'audit', note: 'Identified in AI_SECURITY_PLATFORM_AUDIT_v2.35.md' },
      { timestamp: ts(0), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer', note: 'P2 — Production Readiness Sprint 1. Double-submit cookie + server middleware + frontend interceptor.' },
    ],
    screenshotUrls: [], auditReferences: ['AI_SECURITY_PLATFORM_AUDIT_v2.35.md'],
    versionFound: '2.35.0', versionFixed: '2.36.0',
    reporter: 'audit-system', assignee: 'engineer',
    createdAt: ts(86400000), updatedAt: ts(0),
    isReleaseBlocker: false, isSecurityBlocker: true, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'No automated test suite — integration and E2E coverage near zero',
    description: 'The platform has no integration tests and no end-to-end test suite. Only a small vitest suite covering URL validation (183 tests) was found. Critical paths (auth, pipeline, missions, payments) have no automated regression coverage.',
    module: 'quality', severity: 'critical', status: 'fixed', priority: 'release-blocker',
    affectedFiles: ['artifacts/nomad/', 'artifacts/api-server/', '.github/workflows/ci.yml', 'scripts/ci-check.sh'],
    notes: 'Fixed in Production Readiness Sprint 2 (Build 2.37.0): 6 API integration/security suites (auth, CSRF, security, opportunities, companies, AI privacy) via supertest; 3 new frontend suites (CSRF interceptor, AI cost tracker, performance benchmarks); Testing Center™ in Dev Center; GitHub Actions CI workflow; local ci-check.sh runner. Testing score 85 → 92. 9 pre-existing failures (roamerPassport/XP/backGuard) remain tracked separately.',
    history: [
      { timestamp: ts(86400000), action: 'opened', by: 'audit', note: 'Identified in AI_SECURITY_PLATFORM_AUDIT_v2.35.md — Testing score: 22/100' },
      { timestamp: ts(0), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer', note: 'Production Readiness Sprint 2: Gold Standard Testing Framework implemented. 6 API suites + 3 frontend suites + CI workflow + Testing Center™.' },
    ],
    screenshotUrls: [], auditReferences: ['AI_SECURITY_PLATFORM_AUDIT_v2.35.md'],
    versionFound: '2.35.0', versionFixed: '2.37.0',
    reporter: 'audit-system', assignee: 'engineer',
    createdAt: ts(86400000), updatedAt: ts(0),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'Database secondary indexes missing — will degrade at scale',
    description: 'No secondary indexes defined on user_id columns across operational tables (companies, opportunities, events, income_entries, timers). At scale, every user-scoped query will perform a full table scan. events.occurred_at also lacks an index for time-range queries.',
    module: 'database', severity: 'medium', status: 'fixed', priority: 'medium',
    affectedFiles: ['lib/db/src/schema/events.ts', 'lib/db/src/schema/companies.ts', 'lib/db/src/schema/opportunities.ts', 'lib/db/src/schema/timers.ts', 'lib/db/src/schema/income_entries.ts', 'lib/db/src/schema/sessions.ts', 'lib/db/migrations/0002_add_indexes.sql'],
    notes: 'Fixed (Production Readiness Sprint 1): index() added to all 6 operational tables via Drizzle pgTable 3rd arg. Pushed to live DB. Migration SQL also committed at lib/db/migrations/0002_add_indexes.sql.',
    history: [
      { timestamp: ts(86400000), action: 'opened', by: 'audit', note: 'Identified in AI_SECURITY_PLATFORM_AUDIT_v2.35.md' },
      { timestamp: ts(0), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer', note: 'P3 — Production Readiness Sprint 1. 7 indexes created (user_id × 6 tables + events.occurred_at DESC). Pushed to live DB.' },
    ],
    screenshotUrls: [], auditReferences: ['AI_SECURITY_PLATFORM_AUDIT_v2.35.md'],
    versionFound: '2.35.0', versionFixed: '2.36.0',
    reporter: 'audit-system', assignee: 'engineer',
    createdAt: ts(86400000), updatedAt: ts(0),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'Re-consent prompt not wired to Shell.tsx',
    description: 'termsVersion.ts exports needsReconsent() which checks if the stored acceptance version differs from the current Terms/Privacy version. However, Shell.tsx does not call this on boot — users who need to re-consent are never prompted automatically. LegalPrivacySection does display a banner on the Profile page as a partial workaround, but users who never visit /profile will never see the re-consent prompt.',
    module: 'auth', severity: 'medium', status: 'in-progress', priority: 'medium',
    affectedFiles: ['src/Shell.tsx', 'src/lib/termsVersion.ts', 'src/components/legal/LegalPrivacySection.tsx'],
    notes: 'Partial: LegalPrivacySection shows "Please review updated terms" banner on /profile. Full fix: add useEffect in Shell.tsx calling needsReconsent() on boot; show re-consent modal gated before access.',
    history: [
      { timestamp: ts(86400000 * 2), action: 'opened', by: 'engineer', note: 'Known gap from Trust & Transparency sprint' },
      { timestamp: ts(0),           action: 'status_change', from: 'open', to: 'in-progress', by: 'engineer', note: 'UI/UX polish sprint audit: LegalPrivacySection shows partial prompt on /profile. Full Shell.tsx gate still needed.' },
    ],
    screenshotUrls: [], auditReferences: [],
    versionFound: '2.35.0',
    reporter: 'engineer', assignee: 'engineer',
    createdAt: ts(86400000 * 2), updatedAt: ts(0),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'No formal DB migration files — schema changes deployed via drizzle-kit push',
    description: 'The project has no migration files in lib/db/. Schema changes are applied via drizzle-kit push (schema sync) which can silently drop or alter columns in production. This is unsafe for a production database and makes rollbacks impossible.',
    module: 'database', severity: 'high', status: 'fixed', priority: 'high',
    affectedFiles: ['lib/db/drizzle.config.ts', 'lib/db/package.json', 'lib/db/src/migrate.ts', 'lib/db/migrations/0001_initial_schema.sql', 'lib/db/migrations/0002_add_indexes.sql', 'lib/db/migrations/meta/_journal.json'],
    notes: 'Fixed (Production Readiness Sprint 1): drizzle.config.ts now has out: ./migrations. Baseline migration (0001) + index migration (0002) committed. src/migrate.ts runner added. pnpm --filter @workspace/db migrate applies future changes safely.',
    history: [
      { timestamp: ts(86400000), action: 'opened', by: 'audit', note: 'Identified in AI_SECURITY_PLATFORM_AUDIT_v2.35.md' },
      { timestamp: ts(0), action: 'status_change', from: 'open', to: 'fixed', by: 'engineer', note: 'P4 — Production Readiness Sprint 1. Migration infrastructure in place; 2 SQL files committed.' },
    ],
    screenshotUrls: [], auditReferences: ['AI_SECURITY_PLATFORM_AUDIT_v2.35.md'],
    versionFound: '2.35.0', versionFixed: '2.36.0',
    reporter: 'audit-system', assignee: 'engineer',
    createdAt: ts(86400000), updatedAt: ts(0),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: true, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'Binary blobs stored in IndexedDB — no object storage for large files',
    description: 'Audio recordings, images, and other binary assets captured in the Vault are stored entirely in the browser\'s IndexedDB. This creates storage quota issues on mobile (typically 50–250MB quota), prevents sharing assets across devices, and blocks server-side processing like thumbnail generation or transcription streaming.',
    module: 'storage', severity: 'high', status: 'verified', priority: 'high',
    affectedFiles: ['src/services/KnowledgeStore.ts', 'src/services/FileStorageService.ts'],
    notes: 'RESOLVED Sprint 091 (Build 2.39.0): Cloudflare R2 primary storage via FileStorageManager → R2StorageProvider. SHA-256 dedup, gzip compression, thumbnail generation via BackgroundJobQueue. KnowledgeItemModal handles r2: refs. IndexedDB now metadata-only.',
    history: [
      { timestamp: ts(86400000), action: 'opened', by: 'audit', note: 'Identified in AI_SECURITY_PLATFORM_AUDIT_v2.35.md — Storage score: 67/100' },
      { timestamp: ts(0), action: 'resolved', by: 'engineer', note: 'Sprint 091: R2StorageProvider + FileStorageManager. 4 binary storage sites migrated. 24 server + 21 client tests.' },
      { timestamp: ts(0), action: 'verified', by: 'sprint-094-audit', note: 'Source inspection confirmed R2 pipeline: upload/dedup/compress/thumbnail/signed-URL all operational. Storage Health: 90%.' },
    ],
    screenshotUrls: [], auditReferences: ['AI_SECURITY_PLATFORM_AUDIT_v2.35.md', 'SPRINT_091_STORAGE_AUDIT.md'],
    versionFound: '2.35.0', versionFixed: '2.39.0',
    reporter: 'audit-system', assignee: 'engineer',
    createdAt: ts(86400000), updatedAt: ts(0),
    isReleaseBlocker: false, isSecurityBlocker: false, isDataLossBug: false, isAuthBug: false, isAIBlocker: false,
  },
  {
    title: 'Mission Control PIN blocked by CSRF — /about Operator Authentication 403 on production',
    description: 'POST /api/auth/verify-owner-pin rejected 403 "CSRF token missing". /about is public — no session on GET means no wr_csrf cookie set, interceptor skips header injection, server rejects. Fix: added /auth/verify-owner-pin to EXEMPT_SUFFIXES (same pattern as login/register). PIN is a credential, not a session mutation.',
    module: 'auth',
    severity: 'high',
    status: 'fixed',
    priority: 'release-blocker',
    affectedFiles: ['artifacts/api-server/src/middlewares/csrfProtection.ts'],
    notes: 'Reported by operator via screenshot: "My pin isnt working". CSRF error visible in Brave Mobile. Fix: exempt verify-owner-pin from CSRF middleware. 309/309 tests pass post-fix.',
    history: [
      { timestamp: ts(0), action: 'opened',        by: 'operator', note: 'Screenshot: CSRF token missing on /about PIN form. Brave Mobile, workroamer.com/about.' },
      { timestamp: ts(0), action: 'status_change',  by: 'engineer', note: 'Added /auth/verify-owner-pin to EXEMPT_SUFFIXES in csrfProtection.ts.' },
      { timestamp: ts(0), action: 'status_change',  by: 'engineer', note: '309/309 api-server tests pass. Exemption pattern matches login/register precedent.' },
    ],
    screenshotUrls: [],
    auditReferences: ['csrfProtection.ts EXEMPT_SUFFIXES'],
    versionFound: '2.45.0', versionFixed: '2.46.0',
    reporter: 'operator', assignee: 'engineer',
    createdAt: ts(0), updatedAt: ts(0),
    isReleaseBlocker: true, isSecurityBlocker: false, isDataLossBug: false, isAuthBug: true, isAIBlocker: false,
  },
];

// ─── Storage ──────────────────────────────────────────────────────────────────

let _bugs: Bug[] | null = null;

function uid(): string {
  return `bug-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function load(): Bug[] {
  if (_bugs !== null) return _bugs;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) { _bugs = JSON.parse(raw) as Bug[]; return _bugs; }
  } catch { /* corrupted */ }
  _bugs = [];
  return _bugs;
}

function persist(): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_bugs ?? [])); } catch { /* quota */ }
}

function seed(): void {
  if (localStorage.getItem(SEEDED_KEY)) return;
  const bugs = load();
  if (bugs.length === 0) {
    for (const b of SEED_BUGS) {
      bugs.push({ ...b, id: uid() });
    }
    persist();
  }
  localStorage.setItem(SEEDED_KEY, '1');
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const BugTrackerService = {
  /** Initialise + seed on first use */
  init(): void { seed(); },

  getAll(): Bug[] {
    seed();
    return [...load()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  },

  getById(id: string): Bug | null {
    return load().find(b => b.id === id) ?? null;
  },

  create(draft: Omit<Bug, 'id' | 'createdAt' | 'updatedAt' | 'history'>): Bug {
    const now = new Date().toISOString();
    const bug: Bug = {
      ...draft,
      id:        uid(),
      createdAt: now,
      updatedAt: now,
      history:   [{ timestamp: now, action: 'opened', by: draft.reporter }],
    };
    load().push(bug);
    persist();
    return bug;
  },

  update(id: string, patch: Partial<Omit<Bug, 'id' | 'createdAt'>>, by = 'developer'): Bug | null {
    const bugs = load();
    const idx  = bugs.findIndex(b => b.id === id);
    if (idx < 0) return null;
    const prev = bugs[idx];
    const now  = new Date().toISOString();

    const historyEntry: BugHistoryEntry = { timestamp: now, action: 'updated', by };
    if (patch.status && patch.status !== prev.status) {
      historyEntry.action = 'status_change';
      historyEntry.from   = prev.status;
      historyEntry.to     = patch.status;
    }
    if (patch.severity && patch.severity !== prev.severity) {
      historyEntry.action = 'severity_change';
      historyEntry.from   = prev.severity;
      historyEntry.to     = patch.severity;
    }

    bugs[idx] = { ...prev, ...patch, updatedAt: now, history: [...prev.history, historyEntry] };
    persist();
    _bugs = bugs;
    return bugs[idx];
  },

  markFixed(id: string, version = BUILD_VER, by = 'developer'): Bug | null {
    return BugTrackerService.update(id, { status: 'fixed', versionFixed: version }, by);
  },

  markVerified(id: string, by = 'developer'): Bug | null {
    return BugTrackerService.update(id, { status: 'verified' }, by);
  },

  markDeferred(id: string, note?: string, by = 'developer'): Bug | null {
    const bugs = load();
    const bug  = bugs.find(b => b.id === id);
    if (!bug) return null;
    const now = new Date().toISOString();
    bug.status    = 'deferred';
    bug.updatedAt = now;
    bug.history.push({ timestamp: now, action: 'status_change', from: bug.status, to: 'deferred', by, note });
    persist();
    return bug;
  },

  delete(id: string): void {
    _bugs = load().filter(b => b.id !== id);
    persist();
  },

  search(filter: BugFilter): Bug[] {
    return BugTrackerService.getAll().filter(bug => {
      if (filter.module   && filter.module   !== 'all' && bug.module   !== filter.module)   return false;
      if (filter.severity && filter.severity !== 'all' && bug.severity !== filter.severity) return false;
      if (filter.status   && filter.status   !== 'all' && bug.status   !== filter.status)   return false;
      if (filter.priority && filter.priority !== 'all' && bug.priority !== filter.priority) return false;
      if (filter.search) {
        const q = filter.search.toLowerCase();
        return (
          bug.title.toLowerCase().includes(q)       ||
          bug.description.toLowerCase().includes(q) ||
          bug.notes.toLowerCase().includes(q)
        );
      }
      return true;
    });
  },

  /** Release Gate — checks all blocker conditions */
  getReleaseGate(): ReleaseGateResult {
    const open = BugTrackerService.getAll().filter(b => b.status === 'open' || b.status === 'in-progress');
    const criticalBugs     = open.filter(b => b.severity === 'critical');
    const securityBlockers = open.filter(b => b.isSecurityBlocker);
    const dataLossBugs     = open.filter(b => b.isDataLossBug);
    const authBugs         = open.filter(b => b.isAuthBug);
    const aiBlockers       = open.filter(b => b.isAIBlocker);
    const releaseBlockers  = open.filter(b => b.isReleaseBlocker || b.priority === 'release-blocker');

    const clear = criticalBugs.length === 0 && securityBlockers.length === 0 &&
      dataLossBugs.length === 0 && authBugs.length === 0 &&
      aiBlockers.length === 0 && releaseBlockers.length === 0;

    const issues: string[] = [];
    if (criticalBugs.length)     issues.push(`${criticalBugs.length} critical bug(s)`);
    if (securityBlockers.length) issues.push(`${securityBlockers.length} security blocker(s)`);
    if (dataLossBugs.length)     issues.push(`${dataLossBugs.length} data-loss bug(s)`);
    if (authBugs.length)         issues.push(`${authBugs.length} auth bug(s)`);
    if(releaseBlockers.length)   issues.push(`${releaseBlockers.length} release blocker(s)`);

    const summary = clear
      ? `✅ Release gate clear — no blockers found in ${open.length} open issue(s).`
      : `🚨 Release gate BLOCKED — ${issues.join(', ')} must be resolved before release.`;

    return { clear, criticalBugs, securityBlockers, dataLossBugs, authBugs, aiBlockers, releaseBlockers, summary };
  },

  exportReport(): string {
    const bugs   = BugTrackerService.getAll();
    const gate   = BugTrackerService.getReleaseGate();
    const open   = bugs.filter(b => b.status === 'open' || b.status === 'in-progress');
    const fixed  = bugs.filter(b => b.status === 'fixed' || b.status === 'verified');
    const deferred = bugs.filter(b => b.status === 'deferred');

    const lines = [
      `# WorkRoamer™ Bug Tracker Export`,
      `Generated: ${new Date().toISOString()}`,
      ``,
      `## Summary`,
      `- Total: ${bugs.length}`,
      `- Open: ${open.length}`,
      `- Fixed/Verified: ${fixed.length}`,
      `- Deferred: ${deferred.length}`,
      `- Release Gate: ${gate.clear ? 'CLEAR ✅' : 'BLOCKED 🚨'}`,
      ``,
      `## Open Bugs`,
      ...open.map(b => `- [${b.severity.toUpperCase()}] ${b.title} (${b.module}, ${b.priority})`),
      ``,
      `## Fixed Bugs`,
      ...fixed.map(b => `- ✅ ${b.title} — fixed in ${b.versionFixed ?? 'unknown'}`),
      ``,
      `## Deferred`,
      ...deferred.map(b => `- ⏸ ${b.title}`),
    ];
    return lines.join('\n');
  },

  generateReleaseNotes(version = BUILD_VER): string {
    const fixed = BugTrackerService.getAll().filter(b =>
      (b.status === 'fixed' || b.status === 'verified') && b.versionFixed === version,
    );
    if (!fixed.length) return `No bugs fixed in ${version}.`;
    const lines = [
      `## Bug Fixes — ${version}`,
      ...fixed.map(b => `- ${b.title} [${b.severity}/${b.module}]`),
    ];
    return lines.join('\n');
  },

  /** Summary for audit integration */
  getAuditSummary() {
    const all      = BugTrackerService.getAll();
    const open     = all.filter(b => b.status === 'open' || b.status === 'in-progress');
    const fixed    = all.filter(b => b.status === 'fixed' || b.status === 'verified');
    const deferred = all.filter(b => b.status === 'deferred');
    const critical = open.filter(b => b.severity === 'critical');
    const blockers = open.filter(b => b.isReleaseBlocker || b.priority === 'release-blocker');
    return { total: all.length, open: open.length, fixed: fixed.length, deferred: deferred.length, critical: critical.length, blockers: blockers.length };
  },

  /** Extension point — called by future beta report intake */
  createFromBetaReport(report: BetaReportTemplate): Bug {
    return BugTrackerService.create({
      title:           `[Beta] ${report.actualBehavior.slice(0, 80)}`,
      description:     `Steps: ${report.stepsToReproduce}\n\nExpected: ${report.expectedBehavior}\n\nActual: ${report.actualBehavior}`,
      module:          'platform',
      severity:        report.severity,
      status:          'open',
      priority:        report.severity === 'critical' ? 'release-blocker' : 'medium',
      affectedFiles:   [],
      notes:           `Reported by ${report.reporter} on ${report.device} / ${report.browser}`,
      screenshotUrls:  report.screenshotUrl ? [report.screenshotUrl] : [],
      auditReferences: [],
      versionFound:    report.buildNumber,
      reporter:        report.reporter,
      isReleaseBlocker: false,
      isSecurityBlocker: false,
      isDataLossBug:   false,
      isAuthBug:       false,
      isAIBlocker:     false,
      betaReport:      report,
    });
  },
};
